/**
 * SEO configuration for all pages on the Raidu website
 * 
 * This file contains structured SEO data for each page to ensure
 * consistent metadata across the site without duplication.
 */

export interface SEOData {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  twitterCard?: 'summary' | 'summary_large_image';
  noIndex?: boolean;
  structuredData?: Record<string, any>;
}

// Company information for structured data
const companyInfo = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Raidu",
  "url": "https://www.raidu.com",
  "logo": "https://www.raidu.com/logos/raidu_logo.png",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "271 Little Falls Dr.",
    "addressLocality": "Wilmington",
    "addressRegion": "Delaware",
    "postalCode": "19808",
    "addressCountry": "US"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "availableLanguage": "English"
  },
  "sameAs": [
    "http://linkedin.com/company/raidu-ai",
    "https://x.com/RaiduInc"
  ]
};

// Base SEO data for the website
const baseSEO: SEOData = {
  title: "Raidu | Enterprise AI Governance Platform",
  description: "Raidu is an enterprise AI governance platform that helps organizations adopt AI safely and securely with data masking, prompt improvement, private RAG, and multi-LLM support.",
  ogImage: "/images/raidu-og-image.png",
  ogType: "website",
  structuredData: companyInfo
};

// Homepage SEO
export const homeSEO: SEOData = {
  ...baseSEO,
  title: "Raidu | Enterprise AI Governance Platform",
  description: "Adopt AI safely and securely with Raidu's enterprise AI governance platform featuring data masking, prompt improvement, private RAG, and multi-LLM support.",
  structuredData: {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "url": "https://www.raidu.com",
    "name": "Raidu",
    "description": "Enterprise AI Governance Platform",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.raidu.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  }
};

// About page SEO
export const aboutSEO: SEOData = {
  ...baseSEO,
  title: "About Raidu | Our Mission & Vision",
  description: "Learn about Raidu's mission to make enterprise AI adoption safe, secure, and compliant through our comprehensive governance platform.",
};

// Careers page SEO
export const careersSEO: SEOData = {
  ...baseSEO,
  title: "Careers at Raidu | Join Our Team",
  description: "Join the Raidu team and help shape the future of enterprise AI governance. Explore open positions and our company culture.",
};

// Contact page SEO
export const contactSEO: SEOData = {
  ...baseSEO,
  title: "Contact Raidu | Get in Touch",
  description: "Contact the Raidu team for sales inquiries, support, or press information. We're here to help with your enterprise AI governance needs.",
};

// Press page SEO
export const pressSEO: SEOData = {
  ...baseSEO,
  title: "Raidu Press | News & Media Resources",
  description: "Access the latest news, press releases, and media resources from Raidu, the leading enterprise AI governance platform.",
};

// Blog index page SEO
export const blogSEO: SEOData = {
  ...baseSEO,
  title: "Raidu Blog | AI Governance Insights",
  description: "Explore insights, best practices, and industry trends on enterprise AI governance, compliance, security, and adoption strategies.",
  ogType: "website",
};

// Blog category pages SEO
export const blogCategorySEO = (category: string): SEOData => {
  const formattedCategory = category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  
  return {
    ...baseSEO,
    title: `${formattedCategory} | Raidu Blog`,
    description: `Explore our ${formattedCategory.toLowerCase()} articles for insights on enterprise AI governance, compliance, and secure adoption.`,
    ogType: "website",
  };
};

// Blog post SEO is handled dynamically based on post content

// Booking page SEO
export const bookingSEO: SEOData = {
  ...baseSEO,
  title: "Book a Demo | Raidu Enterprise AI Governance",
  description: "Schedule a personalized demo of Raidu's enterprise AI governance platform and see how we can help your organization adopt AI safely.",
};

// Privacy Policy page SEO
export const privacyPolicySEO: SEOData = {
  ...baseSEO,
  title: "Privacy Policy | Raidu",
  description: "Read Raidu's privacy policy to understand how we collect, use, and protect your personal information.",
};

// Terms of Service page SEO
export const termsSEO: SEOData = {
  ...baseSEO,
  title: "Terms of Service | Raidu",
  description: "Review Raidu's terms of service agreement for using our enterprise AI governance platform and website.",
};

// Industries index page SEO
export const industriesSEO: SEOData = {
  ...baseSEO,
  title: "AI Governance for Industries | Raidu",
  description: "Discover how Raidu's AI governance platform addresses the unique challenges of different industries including finance, healthcare, legal, and more.",
};

// Industry-specific pages SEO
export const financeSEO: SEOData = {
  ...baseSEO,
  title: "AI Governance for Finance | Raidu",
  description: "Secure AI adoption for financial services with Raidu's governance platform. Ensure compliance, data security, and risk management.",
};

export const healthcareSEO: SEOData = {
  ...baseSEO,
  title: "AI Governance for Healthcare | Raidu",
  description: "Enable secure and compliant AI adoption in healthcare with Raidu's governance platform. Protect PHI and ensure HIPAA compliance.",
};

export const legalSEO: SEOData = {
  ...baseSEO,
  title: "AI Governance for Legal | Raidu",
  description: "Empower legal teams with secure AI adoption through Raidu's governance platform. Protect client confidentiality and ensure compliance.",
};

export const educationSEO: SEOData = {
  ...baseSEO,
  title: "AI Governance for Education | Raidu",
  description: "Enable secure AI adoption in education with Raidu's governance platform. Protect student data and ensure regulatory compliance.",
};

export const manufacturingSEO: SEOData = {
  ...baseSEO,
  title: "AI Governance for Manufacturing | Raidu",
  description: "Transform manufacturing with secure AI adoption through Raidu's governance platform. Protect IP and ensure operational security.",
};

export const retailSEO: SEOData = {
  ...baseSEO,
  title: "AI Governance for Retail | Raidu",
  description: "Enhance retail operations with secure AI adoption through Raidu's governance platform. Protect customer data and ensure compliance.",
};

// 404 page SEO
export const notFoundSEO: SEOData = {
  ...baseSEO,
  title: "Page Not Found | Raidu",
  description: "The page you're looking for cannot be found. Return to the Raidu homepage to explore our enterprise AI governance platform.",
};
